import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { MAT_DIALOG_DATA as MAT_DIALOG_DATA, MatDialogRef as MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { DailyReportService } from '../../../../daily-report.service';
import { SharedService } from '../../../../../../../../../../shared/core/shared.service';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';

/**
 * Capture method emitted to the parent. This dialog is draw-only, so capture
 * always emits 'drawn'. The wider 'typed'/'typed_font' values still exist in
 * the data model (see the parent's buildSignatureView) for rendering legacy
 * signatures, but they are no longer produced here.
 */
export type SignatureCaptureMethod = 'drawn' | 'typed_font' | 'typed';

/**
 * Shape emitted on dailyReportService.setSignature. Extends the legacy
 * `{ value, role }` channel with method/timestamp/pictures so the parent can
 * thread the rich signature into the saved payload. `value` maps to
 * `signature_text` in the parent merge; it now carries the PRINTED NAME the
 * signer types alongside the drawn image (for legibility), so a drawn signature
 * can render both the captured strokes AND a readable name.
 *
 * `index` identifies which signer slot in the parent's dynamic `signatures[]`
 * this capture targets. `null`/absent means "append a new signer". `role` is
 * now the role LABEL (e.g. 'Entry Supervisor'), sourced from the form's
 * ui_config.signature_roles, not the legacy 'One'/'Two' positional code.
 *
 * `signature_font` is always null for drawn captures; it is retained on the
 * emission shape only so the parent merge keeps a single field contract.
 */
export interface SignatureEmission {
  value: string;
  role: string | null;
  index: number | null;
  signature_method: SignatureCaptureMethod;
  signature_font: string | null;
  signed_at: string;
  pictures: any[];
}

/**
 * Emitted on confirm in EDIT mode when the user did NOT redraw (role-only edit,
 * or Clear with no new stroke). Carries the (possibly changed) role + the slot
 * index but deliberately omits the capture fields (pictures / signature_method /
 * signed_at / value) so the parent merge preserves the EXISTING signature image
 * and metadata. This is the "keep" half of the keep-vs-replace contract: never
 * re-upload, never blank a slot the user didn't re-sign.
 */
export interface SignatureKeepEmission {
  role: string | null;
  index: number | null;
  keep: true;
  // The (possibly edited) printed name. Carried so a role/name-only edit that
  // keeps the existing drawn image still persists a changed printed name through
  // the parent merge into signature_text.
  value: string;
}

@Component({
    selector: 'app-signature',
    templateUrl: './signature.component.html',
    styleUrls: ['./signature.component.scss'],
    standalone: false
})
export class SignatureComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('signatureCanvas') signatureCanvas: ElementRef<HTMLCanvasElement> | undefined;

  form: FormGroup = new FormGroup({});
  // `printedName` is the legible typed name captured ALONGSIDE the drawn image.
  // Emitted as the signature's signature_text (via the parent's mergeSignatureEmission).
  localForm: FormGroup = new FormGroup({
    role: new FormControl(null),
    printedName: new FormControl(''),
  });
  // Role options are role-label strings sourced from the form's
  // ui_config.signature_roles (label === value), falling back to QA/QC.
  roleOptions: any = [{ label: 'QA', value: 'QA' }, { label: 'QC', value: 'QC' }];
  // The signer slot being edited (index into the parent signatures[]), or null
  // when this dialog is appending a brand-new signer.
  signerIndex: number | null = null;

  // Per-form gate sourced from ui_config.signature_pad.require_printed_name
  // (default false). Only meaningful in draw-pad mode: when true the printed
  // name is mandatory and the Add/Save button stays disabled until it is
  // non-empty (in addition to the existing draw/role gates).
  requirePrintedName = false;

  // Capture mode sourced from ui_config.signature_pad.enabled (default false).
  // true  -> DRAW-PAD: canvas capture + printed-name field (current behavior).
  // false -> TYPED: hide the canvas, capture a single typed signature name
  //          (reusing the printedName control) emitted as signature_method
  //          'typed' with no attachment/image.
  signaturePadEnabled = false;

  // True once at least one stroke has been drawn on the canvas.
  hasDrawing = false;
  uploading = false;

  // EDIT mode: this dialog was opened for an existing signer that already has a
  // drawn signature image. In edit mode the existing signature is shown as a
  // plain <img> overlay filling the pad area (NOT drawn onto the canvas), and
  // confirm is allowed without a new stroke (role-only edit).
  editing = false;
  // Resolved existing signature image (same resolution as buildSignatureView).
  // Rendered as an absolutely-positioned <img> overlay inside the pad so the
  // user SEES their existing signature with no fetch/base64/canvas drawing.
  // A normal <img src=…> loads the S3 image cross-origin fine.
  existingImageUrl: string | null = null;
  // True only once the user lays down a NEW stroke (onPointerDown). Drives the
  // keep-vs-replace decision on confirm: false => keep existing, true => replace.
  // Also hides the overlay <img> so a fresh re-sign shows only the new strokes.
  userDrew = false;
  // True after the user hits Clear (until they draw again). Hides the existing
  // <img> overlay so Clear visibly blanks the pad in edit mode (otherwise the
  // overlay would re-show and Clear would look like a no-op).
  cleared = false;

  private ctx: CanvasRenderingContext2D | null = null;
  private drawing = false;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private dailyReportService: DailyReportService,
    private sharedService: SharedService,
    private commonService: CommonService,
    private cdRef: ChangeDetectorRef,
    public dialogRef: MatDialogRef<SignatureComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    if (data) {
      this.form = data.form;
      this.signerIndex = data.index ?? null;
      this.signaturePadEnabled = !!data.signaturePadEnabled;
      this.requirePrintedName = !!data.requirePrintedName;
      if (Array.isArray(data.options) && data.options.length) {
        this.roleOptions = data.options;
      }
    }
  }

  /**
   * Pure predicate: can the current capture be confirmed?
   * - Never while uploading.
   * - TYPED mode (signaturePadEnabled=false): confirm depends on the typed name
   *   being non-empty (role is gated separately by the dropdown). The canvas is
   *   not rendered, so drawing/editing/requirePrintedName are irrelevant.
   * - DRAW-PAD mode (signaturePadEnabled=true):
   *   - ADD mode (no existing signature): requires at least one new stroke.
   *   - EDIT mode (existing signature present): the user may be doing a role-only
   *     edit and keeping the existing image; a new stroke replaces it but is not
   *     required.
   *   - When `requirePrintedName` is on (per-form ui_config flag) the printed
   *     name must be non-empty in BOTH add and edit modes — including a keep-
   *     without-redraw.
   * Kept free of `this` reads so it stays unit-testable.
   */
  static isConfirmable(
    hasDrawing: boolean,
    uploading: boolean,
    editing = false,
    requirePrintedName = false,
    printedName = '',
    signaturePadEnabled = false,
    role: string | null = null,
  ): boolean {
    if (uploading) {
      return false;
    }
    if (!signaturePadEnabled) {
      // Typed mode: a role must be selected AND the entered text (reusing the
      // printedName control) must be non-empty. Drawing/editing gates do not apply.
      return !!role && !!printedName?.trim();
    }
    if (requirePrintedName && !printedName?.trim()) {
      return false;
    }
    return editing || hasDrawing;
  }

  /**
   * Pure resolution of the existing signer's signature image, mirroring
   * buildSignatureView in the parent. The resolved URL is rendered as an <img>
   * overlay (no canvas drawing), so CORS taint is irrelevant: prefer the
   * directly-displayable URLs (signature_image_url, then the first picture's url
   * fields) and fall back to an inline base64 data URL last.
   */
  static resolveExistingImage(signer: any): string | null {
    if (!signer) {
      return null;
    }
    const picture = signer?.pictures?.[0];
    return (
      signer?.signature_image_url ||
      picture?.url ||
      picture?.uri ||
      picture?.original_url ||
      picture?.preview_url ||
      picture?.thumb_url ||
      signer?.signature_image_base64 ||
      null
    );
  }

  /**
   * Pure builder for the KEEP emission (edit mode, no redraw): carries the
   * (possibly changed) role + printed name + index but omits the image capture
   * fields (pictures / signature_method / signed_at) so the parent merge
   * preserves the existing drawn image / method / signed_at while still applying
   * an edited printed name to signature_text.
   */
  static buildKeepEmission(
    role: string | null,
    index: number | null = null,
    printedName = '',
  ): SignatureKeepEmission {
    return { role, index, keep: true, value: printedName?.trim() ?? '' };
  }

  /**
   * Pure builder: produce the SignatureEmission for a drawn signature.
   * `printedName` is the legible typed name captured alongside the strokes; it
   * is emitted as `value` (which the parent maps to `signature_text`) so the
   * saved drawn signature renders the image AND the printed name. Trimmed; an
   * empty/whitespace name becomes ''.
   */
  static buildDrawnEmission(
    role: string | null,
    attachmentId: any,
    index: number | null = null,
    printedName = '',
  ): SignatureEmission {
    return {
      value: printedName?.trim() ?? '',
      role,
      index,
      signature_method: 'drawn',
      signature_font: null,
      signed_at: new Date().toISOString(),
      pictures: attachmentId != null ? [attachmentId] : [],
    };
  }

  /**
   * Pure builder: produce the SignatureEmission for a TYPED signature. The
   * entered text (reusing the printedName control) becomes `value` (mapped to
   * `signature_text` by the parent merge). No attachment/picture/image is
   * produced and signature_font is null. Trimmed.
   */
  static buildTypedEmission(
    role: string | null,
    typedName: string,
    index: number | null = null,
  ): SignatureEmission {
    return {
      value: typedName?.trim() ?? '',
      role,
      index,
      signature_method: 'typed',
      signature_font: null,
      signed_at: new Date().toISOString(),
      pictures: [],
    };
  }

  get confirmDisabled(): boolean {
    return !SignatureComponent.isConfirmable(
      this.hasDrawing,
      this.uploading,
      this.editing,
      this.requirePrintedName,
      this.localForm.controls.printedName.value ?? '',
      this.signaturePadEnabled,
      this.localForm.controls.role.value ?? null,
    );
  }

  ngOnInit() {
    // Editing an existing signer: prefill the role from the captured signer
    // object passed in by the parent. We are in EDIT mode only when that signer
    // already has a resolvable signature image — a role-only/append slot with no
    // image stays in ADD mode (must draw). The pad is preloaded with the
    // existing image in ngAfterViewInit; re-signing replaces it, otherwise it is
    // kept. The printed name is prefilled from the existing signer's
    // signature_text so an edit shows (and can change) the captured name.
    const signer = this.data?.signer;
    if (signer) {
      this.localForm.patchValue({
        role: signer.role ?? null,
        printedName: signer.signature_text ?? '',
      }, { emitEvent: false });
      this.existingImageUrl = SignatureComponent.resolveExistingImage(signer);
      this.editing = !!this.existingImageUrl;
    }
  }

  ngAfterViewInit() {
    // Size the canvas backing store once the dialog has laid out. The existing
    // signature (edit mode) is shown as an <img> overlay in the template — NOT
    // drawn onto the canvas — so there is nothing to preload here. The canvas
    // only ever holds NEW strokes, which keeps toDataURL() taint-free on save.
    this.initCanvas();
  }

  private initCanvas() {
    const canvas = this.signatureCanvas?.nativeElement;
    if (!canvas) {
      return;
    }
    // Idempotent: if the context is already set up, don't re-scale (calling
    // ctx.scale twice would compound the DPR and push drawn content off-canvas).
    if (this.ctx) {
      return;
    }
    // Match the backing store to the displayed size, scaled by the device
    // pixel ratio so strokes (and the exported PNG) stay crisp on high-DPI
    // tablets. We scale the context, not the coordinate math, so points from
    // pointFrom() (CSS-pixel based) still map correctly. Fall back to the CSS
    // height from the stylesheet (160px) if the box hasn't laid out yet so the
    // backing store is never left at the 300x150 canvas default.
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = rect.width || canvas.clientWidth;
    const cssHeight = rect.height || canvas.clientHeight;
    if (cssWidth && cssHeight) {
      canvas.width = cssWidth * dpr;
      canvas.height = cssHeight * dpr;
    }
    this.ctx = canvas.getContext('2d');
    if (this.ctx) {
      this.ctx.scale(dpr, dpr);
      this.ctx.lineWidth = 2.5;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.strokeStyle = '#060b1e';
    }
  }

  private pointFrom(event: PointerEvent): { x: number; y: number } | null {
    const canvas = this.signatureCanvas?.nativeElement;
    if (!canvas) {
      return null;
    }
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  onPointerDown(event: PointerEvent) {
    if (!this.ctx) {
      this.initCanvas();
    }
    if (!this.ctx) {
      return;
    }
    const point = this.pointFrom(event);
    if (!point) {
      return;
    }
    event.preventDefault();
    this.drawing = true;
    // First NEW stroke in this session flips userDrew -> replace path on confirm
    // AND hides the existing-signature <img> overlay (template binding), so a
    // fresh re-sign shows only the new strokes on the (transparent) canvas.
    if (!this.userDrew) {
      this.userDrew = true;
      // A new stroke ends the "cleared" state (overlay already hidden by userDrew).
      this.cleared = false;
      this.cdRef.detectChanges();
    }
    this.ctx.beginPath();
    this.ctx.moveTo(point.x, point.y);
    this.signatureCanvas?.nativeElement.setPointerCapture?.(event.pointerId);
  }

  onPointerMove(event: PointerEvent) {
    if (!this.drawing || !this.ctx) {
      return;
    }
    const point = this.pointFrom(event);
    if (!point) {
      return;
    }
    event.preventDefault();
    this.ctx.lineTo(point.x, point.y);
    this.ctx.stroke();
    if (!this.hasDrawing) {
      this.hasDrawing = true;
      this.cdRef.detectChanges();
    }
  }

  onPointerUp() {
    this.drawing = false;
  }

  clearCanvas() {
    const canvas = this.signatureCanvas?.nativeElement;
    if (canvas && this.ctx) {
      this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    this.hasDrawing = false;
    this.userDrew = false;
    // Clear truly BLANKS the pad: `cleared` hides the existing-signature <img>
    // overlay too (so the user actually sees an empty pad to re-sign on),
    // mirroring the mobile pad. Drawing a new stroke un-sets `cleared`. With no
    // subsequent stroke, confirm in edit mode still KEEPS the existing signature
    // (we never save a blank); removal stays a separate explicit action (trash).
    this.cleared = true;
    this.cdRef.detectChanges();
  }

  /**
   * Base64 PNG (transparent background) of the current canvas, or null. Safe
   * from CORS taint: the existing signature is an <img> overlay and is never
   * drawn onto the canvas, so the canvas only ever holds NEW (local) strokes.
   */
  private canvasToPng(): string | null {
    const canvas = this.signatureCanvas?.nativeElement;
    if (!canvas || !this.userDrew) {
      return null;
    }
    return canvas.toDataURL('image/png');
  }

  /** Convert a base64 PNG data URL into a File for multipart upload. */
  private dataUrlToFile(dataUrl: string, filename: string): File {
    const [meta, base64] = dataUrl.split(',');
    const mime = /:(.*?);/.exec(meta)?.[1] || 'image/png';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], filename, { type: mime });
  }

  close() {
    this.dialogRef.close();
  }

  add() {
    if (this.confirmDisabled) {
      return;
    }
    const role = this.localForm.controls.role.value;
    const printedName = this.localForm.controls.printedName.value ?? '';

    // TYPED path: draw pad disabled for this form. Emit the typed name with no
    // attachment/image; the parent merge maps `value` -> signature_text.
    if (!this.signaturePadEnabled) {
      this.dailyReportService.setSignature.next(
        SignatureComponent.buildTypedEmission(role, printedName, this.signerIndex),
      );
      this.dialogRef.close();
      return;
    }

    // KEEP path: edit mode and the user did NOT draw a new stroke (role-only or
    // printed-name-only edit, or Clear with no redraw). Emit keep so the parent
    // preserves the existing image/method/signed_at while applying the
    // (possibly edited) printed name; never re-upload, never blank.
    if (!this.userDrew) {
      this.dailyReportService.setSignature.next(
        SignatureComponent.buildKeepEmission(role, this.signerIndex, printedName),
      );
      this.dialogRef.close();
      return;
    }

    // REPLACE path: a new stroke exists. Upload the PNG, then emit with the
    // attachment id (new pictures / drawn / signed_at).
    const png = this.canvasToPng();
    if (!png) {
      return;
    }
    const file = this.dataUrlToFile(png, `signature_${Date.now()}.png`);
    const fd = new FormData();
    fd.append('inputFiles', file);

    this.uploading = true;
    this.sharedService.uploadFile(fd)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (response: any) => {
          this.uploading = false;
          const attachmentId = response?.data?.[0]?.id ?? null;
          const emission = SignatureComponent.buildDrawnEmission(
            role,
            attachmentId,
            this.signerIndex,
            printedName,
          );
          this.dailyReportService.setSignature.next(emission);
          this.dialogRef.close();
        },
        error: () => {
          this.uploading = false;
          this.commonService.notification('Failed to upload signature. Please try again.', 'error');
        },
      });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
